/**
 * Client for the ranked submission server (Phase 7J; protocol in
 * server/http.ts and docs/security/DEPLOYMENT.md).
 *
 * The client holds no secret that matters for integrity: the server replays
 * every submission itself. The player token is a pseudonymous bearer token
 * kept in this browser (localStorage); losing it only means a new identity.
 * Every call is bounded by a timeout, and every failure is reported as a
 * RankedError the game can show, so an unreachable server never blocks play.
 */

export interface RankedTrack {
  key: string;
  title: string;
  fingerprint: string;
  recordKey: string;
}

export interface RankedGrant {
  attemptId: string;
  nonce: string;
  issuedAt: number;
  expiresAt: number;
  track: RankedTrack;
}

export type RankedStatus = 'accepted' | 'held' | 'rejected' | 'revoked';

export interface RankedDecision {
  submissionId: string;
  status: RankedStatus;
  reason: string;
  message: string;
  /** The server's own time for the run (null when the run was not valid). */
  seconds: number | null;
}

export class RankedError extends Error {
  constructor(
    /** HTTP status, or 0 when the server could not be reached. */
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfterS: number | null = null,
  ) {
    super(message);
  }
}

interface Envelope<T> {
  ok?: boolean;
  data?: T;
  error?: { code?: string; message?: string };
}

interface Identity {
  playerId: string;
  token: string;
}

const STORAGE_KEY = 'xernous.ranked.player';

export interface RankedClientOptions {
  base?: string;
  timeoutMs?: number;
  fetch?: typeof fetch;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

function defaultStorage(): RankedClientOptions['storage'] {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export class RankedClient {
  private readonly base: string;
  private readonly timeoutMs: number;
  private readonly doFetch: typeof fetch;
  private readonly storage: RankedClientOptions['storage'];
  private memoryIdentity: Identity | null = null;

  constructor(o: RankedClientOptions = {}) {
    this.base = (o.base ?? '/api/v1').replace(/\/$/, '');
    this.timeoutMs = o.timeoutMs ?? 15000;
    this.doFetch = o.fetch ?? ((input, init) => fetch(input, init));
    this.storage = o.storage === undefined ? defaultStorage() : o.storage;
  }

  /** The ranked tracks, or null when the server cannot be reached (offline play continues). */
  async tracks(timeoutMs = 3000): Promise<RankedTrack[] | null> {
    try {
      const data = await this.call<RankedTrack[]>('GET', '/tracks', { timeoutMs });
      return Array.isArray(data) ? data : null;
    } catch {
      return null;
    }
  }

  /** Issues a ranked attempt for a track (registering a pseudonymous player first if needed). */
  async attempt(trackKey: string): Promise<RankedGrant> {
    return this.authed((token) => this.call<RankedGrant>('POST', '/attempts', { token, json: { trackKey } }));
  }

  /** Submits a finished recording on its attempt; the server replays it and decides. */
  async submit(grant: RankedGrant, recording: string): Promise<RankedDecision> {
    const id = this.identity();
    if (!id) throw new RankedError(401, 'unauthenticated', 'The ranked identity of this browser was lost; start a new ranked attempt.');
    return this.call<RankedDecision>('POST', `/attempts/${encodeURIComponent(grant.attemptId)}/submission`, { token: id.token, body: recording, headers: { 'X-Attempt-Nonce': grant.nonce }, timeoutMs: Math.max(this.timeoutMs, 60000) });
  }

  /** Accepted records (development servers only). */
  async devScores(): Promise<Record<string, unknown>[]> {
    return this.call<Record<string, unknown>[]>('GET', '/dev/scores');
  }

  private async authed<T>(fn: (token: string) => Promise<T>): Promise<T> {
    const id = this.identity() ?? (await this.register());
    try {
      return await fn(id.token);
    } catch (err: unknown) {
      // The server no longer knows this identity (a fresh database): register again, once.
      if (!(err instanceof RankedError) || err.status !== 401) throw err;
      return fn((await this.register()).token);
    }
  }

  private async register(): Promise<Identity> {
    const id = await this.call<Identity>('POST', '/players');
    if (typeof id?.token !== 'string' || typeof id.playerId !== 'string') throw new RankedError(502, 'bad-response', 'The ranked server sent an unexpected reply.');
    this.memoryIdentity = { playerId: id.playerId, token: id.token };
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.memoryIdentity));
    } catch {
      // Storage refused (private mode, quota): the identity lasts for this page only.
    }
    return this.memoryIdentity;
  }

  private identity(): Identity | null {
    if (this.memoryIdentity) return this.memoryIdentity;
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      const v = raw ? (JSON.parse(raw) as Partial<Identity>) : null;
      if (v && typeof v.token === 'string' && typeof v.playerId === 'string') this.memoryIdentity = { playerId: v.playerId, token: v.token };
    } catch {
      return null;
    }
    return this.memoryIdentity;
  }

  private async call<T>(method: 'GET' | 'POST', path: string, o: { token?: string; json?: unknown; body?: string; headers?: Record<string, string>; timeoutMs?: number } = {}): Promise<T> {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), o.timeoutMs ?? this.timeoutMs);
    const headers: Record<string, string> = { ...o.headers };
    if (o.token) headers['Authorization'] = `Bearer ${o.token}`;
    if (o.json !== undefined) headers['Content-Type'] = 'application/json';
    let res: Response;
    try {
      res = await this.doFetch(`${this.base}${path}`, { method, headers, body: o.json !== undefined ? JSON.stringify(o.json) : o.body, signal: ctrl.signal, credentials: 'omit' });
    } catch (err: unknown) {
      const timedOut = ctrl.signal.aborted;
      throw new RankedError(0, timedOut ? 'timeout' : 'offline', timedOut ? 'The ranked server did not answer in time.' : `The ranked server could not be reached${err instanceof Error && err.message ? ` (${err.message})` : ''}.`);
    } finally {
      clearTimeout(timer);
    }
    const body = await res.json().then(
      (v: unknown) => v as Envelope<T> | null,
      () => null,
    );
    if (res.ok && body?.ok) return body.data as T;
    const retry = Number(res.headers.get('Retry-After'));
    throw new RankedError(res.status, body?.error?.code ?? 'http-error', body?.error?.message ?? `The ranked server answered ${res.status}.`, Number.isFinite(retry) && retry > 0 ? retry : null);
  }
}
